import { Link } from "react-router";

import { GuideArticle, Section } from "../GuideShell";

interface Stage {
  id: string;
  title: string;
  produces: string;
  link?: { to: string; label: string };
}

const STAGES: Stage[] = [
  {
    id: "brief",
    title: "Brief",
    produces: "A captured statement of the role, salary, stakeholders, and constraints.",
  },
  {
    id: "blueprint",
    title: "Role blueprint",
    produces:
      "5–7 capability dimensions with a 1–10 required level on each, ordered by importance.",
    link: { to: "/help/role-blueprint", label: "Role blueprint" },
  },
  {
    id: "market-map",
    title: "Market map & search",
    produces:
      "Target sectors and companies, boolean search strings, and a search shape the longlist will be built from.",
  },
  {
    id: "longlist",
    title: "Longlist",
    produces: "A pool of 30–80 candidates drawn from search, referrals, and the database.",
  },
  {
    id: "outreach",
    title: "Outreach",
    produces:
      "Logged outreach attempts, responses, and a maintained engagement state per candidate.",
  },
  {
    id: "assessment",
    title: "Recruiter assessment",
    produces:
      "A versioned assessment per candidate: evidence-backed scores against the blueprint's capability dimensions.",
    link: { to: "/help/anchors", label: "Behavioural anchors" },
  },
  {
    id: "matrix",
    title: "Assessment matrix",
    produces: "Per-dimension band definitions used to calibrate scoring across assessors.",
    link: { to: "/help/assessment-matrix", label: "Assessment matrix" },
  },
  {
    id: "interviews",
    title: "Interview rounds",
    produces:
      "Structured interview contributions (notes, transcripts, forms) and a round synthesis: per-dimension evidence, scores, and coverage gaps.",
    link: { to: "/help/round-synthesis", label: "Interview round synthesis" },
  },
  {
    id: "offer",
    title: "Offer",
    produces: "A captured offer with stakeholders and decision trail.",
  },
  {
    id: "onboard",
    title: "Onboard",
    produces: "Placement is closed; post-placement notes and references are retained.",
  },
];

export function SelectionProcessPage() {
  return (
    <GuideArticle
      slug="selection-process"
      title="Selection process overview"
      intro={
        <p>
          A campaign moves through ten stages, each one producing an explicit artefact the next
          stage depends on. The stages aren't a checklist — they're a chain of evidence, with every
          decision traceable to what came before.
        </p>
      }
    >
      <Section id="ten-stages" title="The ten stages">
        <ol className="space-y-4">
          {STAGES.map((stage, idx) => (
            <li
              key={stage.id}
              id={stage.id}
              className="scroll-mt-20 rounded-md border border-border p-4"
            >
              <div className="flex items-baseline gap-3">
                <span className="text-xs font-medium text-text-faint">{idx + 1}.</span>
                <h3 className="text-base font-medium text-text-primary">{stage.title}</h3>
              </div>
              <p className="mt-2 text-sm text-text-secondary">{stage.produces}</p>
              {stage.link ? (
                <p className="mt-2 text-xs">
                  <Link to={stage.link.to} className="text-link hover:underline">
                    More on {stage.link.label} →
                  </Link>
                </p>
              ) : null}
            </li>
          ))}
        </ol>
      </Section>

      <Section id="why-stages" title="Why this shape">
        <p>
          Each stage takes a structured input and produces a structured output that is the input to
          the next. The blueprint sets the bar; the assessment scores candidates against that bar;
          the interview round synthesises new evidence; the assessment is regenerated incorporating
          that evidence. No stage produces a free-text verdict that the next stage has to interpret
          — every artefact is a first-class entity that subsequent stages can read, compare, and
          version.
        </p>
        <p>
          This is what makes the trail defensible. When the question comes back six months later —
          "why did we hire this person" or "why didn't we" — the path runs cleanly from brief to
          decision, through artefacts that were the explicit basis for the next step.
        </p>
      </Section>
    </GuideArticle>
  );
}
